/**
 * Input abstraction.
 *
 * Gameplay code NEVER reads a key code. It reads semantic actions. That is what
 * makes adding gamepad and touch later a one-file change instead of a rewrite,
 * and it is why `playerIndex` is threaded through from the start even though v1
 * is single-player (see the co-op decision in the plan).
 *
 * v1 implements keyboard + mouse. GamepadSource and TouchSource are registered
 * but inert - the stubs exist so the wiring is proven and an agent adding them
 * has an obvious place to work.
 *
 * EDGE DETECTION: DOM events arrive between fixed simulation steps, so a quick
 * tap could otherwise be missed entirely. Sources accumulate presses into a
 * pending set which `sampleForStep()` drains, guaranteeing no input is ever
 * dropped regardless of frame rate.
 */

export type ButtonAction =
  | 'dash'
  | 'pounce'
  | 'interact'
  | 'instinct'
  | 'crouch'
  | 'jump'
  | 'toggleEditor'
  | 'pause';

export interface InputFrame {
  /** -1..1, strafe. */
  moveX: number;
  /** -1..1, forward is +1. */
  moveZ: number;
  /** Accumulated look delta since the last step, in radians. */
  lookDeltaX: number;
  lookDeltaY: number;
  /** Currently held. */
  down: Set<ButtonAction>;
  /** Went down since the last step. Never dropped, even on a 5ms tap. */
  justPressed: Set<ButtonAction>;
  /** Came up since the last step. */
  justReleased: Set<ButtonAction>;
}

export interface InputSource {
  readonly name: string;
  readonly enabled: boolean;
  attach(target: HTMLElement): void;
  detach(): void;
  /** Merge this source's state into the frame being assembled. */
  contribute(frame: InputFrame): void;
  /** Clear per-step accumulators. Called after every contribute. */
  endStep(): void;
}

/* ------------------------------------------------------------------ */
/* Keyboard + mouse                                                    */
/* ------------------------------------------------------------------ */

/** Physical `KeyboardEvent.code` values, so the bindings work on any layout. */
const KEY_BINDINGS: Record<string, ButtonAction> = {
  ShiftLeft: 'dash',
  ShiftRight: 'dash',
  Space: 'jump',
  KeyE: 'interact',
  KeyQ: 'instinct',
  ControlLeft: 'crouch',
  KeyC: 'crouch',
  F1: 'toggleEditor',
  Escape: 'pause',
};

export class KeyboardMouseSource implements InputSource {
  readonly name = 'keyboard-mouse';
  enabled = true;

  private held = new Set<string>();
  private pendingPress = new Set<ButtonAction>();
  private pendingRelease = new Set<ButtonAction>();
  private lookX = 0;
  private lookY = 0;
  private pointerLocked = false;
  private target: HTMLElement | null = null;

  /** Mouse sensitivity in radians per pixel. */
  sensitivity = 0.0026;

  attach(target: HTMLElement): void {
    this.target = target;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    target.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.target?.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    this.target = null;
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    // F1 would open browser help; Space would scroll the page.
    if (event.code === 'F1' || event.code === 'Space') event.preventDefault();
    if (this.held.has(event.code)) return; // ignore auto-repeat
    this.held.add(event.code);
    const action = KEY_BINDINGS[event.code];
    if (action) this.pendingPress.add(action);
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(event.code);
    const action = KEY_BINDINGS[event.code];
    if (action) this.pendingRelease.add(action);
  };

  /** Alt-tabbing away must not leave the cat sprinting forever. */
  private onBlur = (): void => {
    for (const code of this.held) {
      const action = KEY_BINDINGS[code];
      if (action) this.pendingRelease.add(action);
    }
    this.held.clear();
  };

  private onMouseDown = (event: MouseEvent): void => {
    if (event.button === 0) {
      this.pendingPress.add('pounce');
      // Request pointer lock on first click so mouse-look works without a
      // visible cursor. The browser requires a user gesture for this.
      if (!this.pointerLocked) void this.target?.requestPointerLock?.();
    }
    if (event.button === 2) this.pendingPress.add('interact');
  };

  private onMouseUp = (event: MouseEvent): void => {
    if (event.button === 0) this.pendingRelease.add('pounce');
    if (event.button === 2) this.pendingRelease.add('interact');
  };

  private onMouseMove = (event: MouseEvent): void => {
    if (!this.pointerLocked) return;
    this.lookX += event.movementX * this.sensitivity;
    this.lookY += event.movementY * this.sensitivity;
  };

  private onPointerLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement === this.target;
  };

  contribute(frame: InputFrame): void {
    if (!this.enabled) return;

    // WASD and arrows both, because a 7-year-old will reach for arrows.
    let x = 0;
    let z = 0;
    if (this.held.has('KeyW') || this.held.has('ArrowUp')) z += 1;
    if (this.held.has('KeyS') || this.held.has('ArrowDown')) z -= 1;
    if (this.held.has('KeyD') || this.held.has('ArrowRight')) x += 1;
    if (this.held.has('KeyA') || this.held.has('ArrowLeft')) x -= 1;

    // Normalize so diagonal movement is not faster than cardinal.
    const length = Math.hypot(x, z);
    if (length > 1) {
      x /= length;
      z /= length;
    }

    frame.moveX += x;
    frame.moveZ += z;
    frame.lookDeltaX += this.lookX;
    frame.lookDeltaY += this.lookY;

    for (const [code, action] of Object.entries(KEY_BINDINGS)) {
      if (this.held.has(code)) frame.down.add(action);
    }
    for (const action of this.pendingPress) frame.justPressed.add(action);
    for (const action of this.pendingRelease) frame.justReleased.add(action);

    // Held mouse buttons are tracked via press/release only, so mirror them.
    if (this.pendingPress.has('pounce')) frame.down.add('pounce');
  }

  endStep(): void {
    this.pendingPress.clear();
    this.pendingRelease.clear();
    this.lookX = 0;
    this.lookY = 0;
  }
}

/* ------------------------------------------------------------------ */
/* Stubs - deliberately inert. See docs/TASKS for the briefs.         */
/* ------------------------------------------------------------------ */

/**
 * Gamepad support. Cheap to implement on top of this action layer and much
 * easier for a 6-year-old than keyboard - see the plan's open items.
 * TODO(T-gamepad): poll navigator.getGamepads(), map left stick to moveX/moveZ,
 * right stick to look, A=jump, X=pounce, RT=dash, LT=crouch, B=interact, Y=instinct.
 */
export class GamepadSource implements InputSource {
  readonly name = 'gamepad';
  readonly enabled = false;
  attach(): void {}
  detach(): void {}
  contribute(): void {}
  endStep(): void {}
}

/**
 * Touch support for phones and tablets: on-screen thumbstick plus a
 * context-sensitive action button whose label changes as the cat approaches
 * things ('PUSH', 'GRAB', 'HIDE').
 * TODO(T-touch): implement in M7 alongside the responsive UI pass.
 */
export class TouchSource implements InputSource {
  readonly name = 'touch';
  readonly enabled = false;
  attach(): void {}
  detach(): void {}
  contribute(): void {}
  endStep(): void {}
}

/* ------------------------------------------------------------------ */
/* Manager                                                             */
/* ------------------------------------------------------------------ */

export class InputManager {
  private sources: InputSource[] = [];
  private frame: InputFrame = createEmptyFrame();

  /** Reserved for co-op. v1 always uses slot 0. */
  constructor(readonly playerIndex = 0) {}

  add(source: InputSource, target: HTMLElement): this {
    source.attach(target);
    this.sources.push(source);
    return this;
  }

  detachAll(): void {
    for (const source of this.sources) source.detach();
    this.sources.length = 0;
  }

  /**
   * Assembles the input for one fixed simulation step and drains the sources'
   * accumulators. Call EXACTLY once per fixed step.
   */
  sampleForStep(): InputFrame {
    const frame = this.frame;
    frame.moveX = 0;
    frame.moveZ = 0;
    frame.lookDeltaX = 0;
    frame.lookDeltaY = 0;
    frame.down.clear();
    frame.justPressed.clear();
    frame.justReleased.clear();

    for (const source of this.sources) {
      source.contribute(frame);
      source.endStep();
    }

    // Clamp after merging, in case two sources both push in one direction.
    const length = Math.hypot(frame.moveX, frame.moveZ);
    if (length > 1) {
      frame.moveX /= length;
      frame.moveZ /= length;
    }

    return frame;
  }

  /** Read-only view of the last assembled frame, for the debug HUD. */
  get lastFrame(): Readonly<InputFrame> {
    return this.frame;
  }
}

export function createEmptyFrame(): InputFrame {
  return {
    moveX: 0,
    moveZ: 0,
    lookDeltaX: 0,
    lookDeltaY: 0,
    down: new Set(),
    justPressed: new Set(),
    justReleased: new Set(),
  };
}
